import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AdminStorageFeesFacade } from './admin-storage-fees.facade';
import {
  EffectiveStorageFeeSettings,
  StorageFeeInvoiceListItem,
  StorageFeeListFilter,
  StorageFeeOverridesDto,
  StorageFeeSettings,
  StorageFeeSettingsUpdateDto,
} from '@models/resources/storage-fee.model';
import {
  DEMO_STORAGE_FEE_INVOICES,
  DEMO_STORAGE_FEE_OVERRIDES,
  DEMO_STORAGE_FEE_SETTINGS_DEFAULTS,
} from '@data/fixtures/storage-fees.fixtures';
import { DEMO_AUDIT_LOGS } from '@data/fixtures/audit-logs.fixtures';
import { dummyError, dummyResponse } from '@data/dummy-response.util';

/**
 * In-Memory-Variante. Die Stores sind mutable Kopien der Fixtures, damit
 * `saveSettings`, `saveOverrides`, `markPaid`, `markUnpaid` und
 * `markPaidBulk` für Folge-Calls (Re-Load der Liste, AuditHistory)
 * sichtbar bleiben.
 *
 * Audit-Logging matched ADR-003 D4 + Konzept §5.9: jede Mark-Paid /
 * Mark-Unpaid-Mutation legt einen polymorphen Eintrag (`StorageFeeInvoice`,
 * `paid_at_set`/`paid_at_cleared`) in `DEMO_AUDIT_LOGS` ab. Der
 * Dummy-AuditLog-Service liest aus dieser Quelle, damit die
 * AuditHistory-Komponente in der Customer-Detail-Sektion die neuen
 * Einträge sofort zeigt.
 */
@Injectable()
export class DummyAdminStorageFeesFacade implements AdminStorageFeesFacade {
  private static readonly STAFF_USER = {
    id: 42,
    email: 'staff@aurumconsulting.at',
    role: 'staff' as const,
  };

  private settings: StorageFeeSettings = { ...DEMO_STORAGE_FEE_SETTINGS_DEFAULTS };

  private readonly overrides: Map<number, StorageFeeOverridesDto> = new Map(
    [...DEMO_STORAGE_FEE_OVERRIDES.entries()].map(([id, o]) => [id, { ...o }]),
  );

  private readonly invoices: StorageFeeInvoiceListItem[] = DEMO_STORAGE_FEE_INVOICES.map(
    (inv) => ({ ...inv, customer: { ...inv.customer } }),
  );

  private nextAuditId = 5000;

  // --- Settings ------------------------------------------------------
  getSettings(): Observable<StorageFeeSettings> {
    return dummyResponse('admin.storageFees', { ...this.settings });
  }

  saveSettings(dto: StorageFeeSettingsUpdateDto): Observable<StorageFeeSettings> {
    this.settings = { ...dto };
    return dummyResponse('admin.storageFees', { ...this.settings });
  }

  // --- Overrides -----------------------------------------------------
  getOverrides(customerId: number): Observable<StorageFeeOverridesDto> {
    const o = this.overrides.get(customerId) ?? {
      interval_months: null,
      start_date: null,
      annual_percentage: null,
    };
    return dummyResponse('admin.storageFees', { ...o });
  }

  saveOverrides(
    customerId: number,
    dto: StorageFeeOverridesDto,
  ): Observable<StorageFeeOverridesDto> {
    this.overrides.set(customerId, { ...dto });
    return dummyResponse('admin.storageFees', { ...dto });
  }

  getEffective(customerId: number): Observable<EffectiveStorageFeeSettings> {
    const o = this.overrides.get(customerId) ?? {
      interval_months: null,
      start_date: null,
      annual_percentage: null,
    };
    const effective: EffectiveStorageFeeSettings = {
      interval_months: o.interval_months ?? this.settings.default_interval_months,
      interval_months_source: o.interval_months !== null ? 'override' : 'default',
      start_date: o.start_date ?? this.settings.default_start_date,
      start_date_source: o.start_date !== null ? 'override' : 'default',
      annual_percentage:
        o.annual_percentage ?? this.settings.default_annual_percentage,
      annual_percentage_source:
        o.annual_percentage !== null ? 'override' : 'default',
    };
    return dummyResponse('admin.storageFees', effective);
  }

  // --- Rechnungen ----------------------------------------------------
  list(filter: StorageFeeListFilter): Observable<StorageFeeInvoiceListItem[]> {
    const { status, customer_id: customerId, year } = filter;
    const filtered = this.invoices.filter((inv) => {
      if (status && status !== 'all') {
        const isPaid = inv.paid_at !== null;
        if (status === 'paid' && !isPaid) return false;
        if (status === 'sent' && isPaid) return false;
      }
      if (customerId !== undefined && inv.customer_id !== customerId) return false;
      if (year !== undefined) {
        const periodYear = Number.parseInt(inv.period_end.slice(0, 4), 10);
        if (periodYear !== year) return false;
      }
      return true;
    });
    return dummyResponse(
      'admin.storageFees',
      filtered.map((inv) => ({ ...inv, customer: { ...inv.customer } })),
    );
  }

  listForCustomer(customerId: number): Observable<StorageFeeInvoiceListItem[]> {
    const forCustomer = this.invoices
      .filter((inv) => inv.customer_id === customerId)
      .sort((a, b) => b.period_end.localeCompare(a.period_end));
    return dummyResponse(
      'admin.storageFees',
      forCustomer.map((inv) => ({ ...inv, customer: { ...inv.customer } })),
    );
  }

  markPaid(id: number): Observable<StorageFeeInvoiceListItem> {
    const index = this.invoices.findIndex((inv) => inv.id === id);
    if (index === -1) return dummyError('admin.storageFees');
    const before = this.invoices[index];
    if (before.paid_at !== null) return dummyError('admin.storageFees');
    const now = new Date().toISOString();
    const updated: StorageFeeInvoiceListItem = { ...before, paid_at: now };
    this.invoices[index] = updated;
    this.appendAuditLog(id, 'paid_at_set', { paid_at: null }, { paid_at: now });
    return dummyResponse('admin.storageFees', {
      ...updated,
      customer: { ...updated.customer },
    });
  }

  markUnpaid(id: number): Observable<StorageFeeInvoiceListItem> {
    const index = this.invoices.findIndex((inv) => inv.id === id);
    if (index === -1) return dummyError('admin.storageFees');
    const before = this.invoices[index];
    if (before.paid_at === null) return dummyError('admin.storageFees');
    const updated: StorageFeeInvoiceListItem = { ...before, paid_at: null };
    this.invoices[index] = updated;
    this.appendAuditLog(
      id,
      'paid_at_cleared',
      { paid_at: before.paid_at },
      { paid_at: null },
    );
    return dummyResponse('admin.storageFees', {
      ...updated,
      customer: { ...updated.customer },
    });
  }

  markPaidBulk(ids: readonly number[]): Observable<StorageFeeInvoiceListItem[]> {
    const now = new Date().toISOString();
    const updated: StorageFeeInvoiceListItem[] = [];
    for (const id of ids) {
      const index = this.invoices.findIndex((inv) => inv.id === id);
      if (index === -1) continue;
      const before = this.invoices[index];
      if (before.paid_at !== null) continue; // Skip already-paid invoices
      const next: StorageFeeInvoiceListItem = { ...before, paid_at: now };
      this.invoices[index] = next;
      this.appendAuditLog(id, 'paid_at_set', { paid_at: null }, { paid_at: now });
      updated.push({ ...next, customer: { ...next.customer } });
    }
    return dummyResponse('admin.storageFees', updated);
  }

  download(_id: number): Observable<Blob> {
    // Sprint-5/6-Stub: leerer Blob, BE-Anschluss liefert die echten Bytes.
    return dummyResponse('admin.storageFees', new Blob([], { type: 'application/pdf' }));
  }

  resend(_id: number): Observable<void> {
    return dummyResponse('admin.storageFees', undefined as void);
  }

  // --- Helpers -------------------------------------------------------
  private appendAuditLog(
    invoiceId: number,
    action: 'paid_at_set' | 'paid_at_cleared',
    oldValues: Record<string, unknown>,
    newValues: Record<string, unknown>,
  ): void {
    DEMO_AUDIT_LOGS.push({
      id: this.nextAuditId++,
      user_id: DummyAdminStorageFeesFacade.STAFF_USER.id,
      auditable_type: 'StorageFeeInvoice',
      auditable_id: invoiceId,
      action,
      old_values: oldValues,
      new_values: newValues,
      comment: null,
      ip_address: '10.8.1.2',
      created_at: new Date().toISOString(),
      user: DummyAdminStorageFeesFacade.STAFF_USER,
    });
  }
}
